/**
 * The medication administration record — giving, holding and stopping a drug.
 *
 * Replaces the clinical half of the `MedicationAdmin` keyon envelope:
 * `MedSignOff`, `PrnSignOff`, `InsertUpdateMedLogTimes`,
 * `ChangeOrderForMedLogTimes`, `HoldMedication`, `ScheduleHoldMedication`,
 * `CheckRemoveScheduledHold`, `ClearCurrentMedicationHold` and
 * `DiscontinueOrder`. Four of those branches were 941, 610, 525 and 386 lines
 * long and each fused persistence, order cloning, order-number allocation and
 * e-mail fan-out into one request.
 *
 * Every call here carries `medications:administer`, and the scope is only half
 * the gate: the caller must also hold **write-level** access to the chart
 * through `care_assignments`. A read-only assignment — a consulting clinician, a
 * linked contact reading the chart — is not a licence to give somebody a drug.
 * `MedSignOff` would sign a dose for any patient id a caller typed.
 *
 * Every state change is a sub-resource rather than a `status` field, so the
 * dose-status ladder cannot be side-stepped by a PATCH.
 */

import api from './client';

// --- the MAR grid ----------------------------------------------------------

/**
 * `?date=` for one day, `?from=&to=` for a window; `?filter[status]=` and
 * `?filter[medication_id]=` narrow it. There was no legacy read of this grid
 * that did not also mutate the chart.
 */
export function listMedicationAdministrations(patientId, params = {}) {
  return api.list(`/patients/${patientId}/medication-administrations`, params);
}

// --- recording doses -------------------------------------------------------

/**
 * Scheduled doses, as `{administered_on, entries: [{slot_time, status, …}]}`.
 * The signer is the token, never a name in the body.
 *
 * One call is one medication. The legacy `MedSignOff` signed a batch spanning
 * several medications in a single post, which is why a partial failure left
 * some slots signed and others not with nothing saying which.
 */
export function recordAdministration(medicationId, payload) {
  return api.post(`/medications/${medicationId}/administrations`, payload);
}

/**
 * PRN doses, which have no scheduled slot. A separate endpoint because a rescue
 * dose is an event rather than a slot, so two of them may share a clock time.
 */
export function recordPrnAdministration(medicationId, payload) {
  return api.post(`/medications/${medicationId}/prn-administrations`, payload);
}

// --- when doses are due ----------------------------------------------------

/**
 * Replace the slot list for one medication, deletions included — hence PUT.
 * Removal is soft and only touches slots still pending.
 */
export function replaceAdministrationSchedule(medicationId, payload) {
  return api.put(`/medications/${medicationId}/administration-schedule`, payload);
}

/**
 * The same rewrite addressed by the **order** rather than the medication line,
 * for the callers that hold an order number and not a medication id.
 */
export function replaceMedicationOrderSchedule(orderId, payload) {
  return api.put(`/medication-orders/${orderId}/schedule`, payload);
}

// --- holds -----------------------------------------------------------------

export function holdMedication(medicationId, payload) {
  return api.post(`/medications/${medicationId}/holds`, payload);
}

/**
 * Resume: a DELETE on the active hold, not a `resume` verb. `reason` rides the
 * query string and is still recorded on the line, so "why did this resume on the
 * 20th?" has an answer — `AutoClearMedHoldTimes` recorded nothing at all.
 */
export function clearMedicationHold(medicationId, reason) {
  return api.delete(`/medications/${medicationId}/holds/current`, {
    params: reason ? { reason } : {},
  });
}

export function listScheduledHolds(medicationId, params = {}) {
  return api.get(`/medications/${medicationId}/scheduled-holds`, params);
}

export function scheduleHold(medicationId, payload) {
  return api.post(`/medications/${medicationId}/scheduled-holds`, payload);
}

/** Top-level: a queued hold has an id and a state of its own. */
export function updateScheduledHold(holdId, patch) {
  return api.patch(`/scheduled-holds/${holdId}`, patch);
}

export function cancelScheduledHold(holdId) {
  return api.delete(`/scheduled-holds/${holdId}`);
}

// --- stopping --------------------------------------------------------------

/**
 * `medications:administer`, not `write`: stopping a drug a patient is taking is
 * a clinical act on that patient.
 */
export function discontinueMedication(medicationId, payload) {
  return api.post(`/medications/${medicationId}/discontinuation`, payload);
}

export default {
  listMedicationAdministrations,
  recordAdministration, recordPrnAdministration,
  replaceAdministrationSchedule, replaceMedicationOrderSchedule,
  holdMedication, clearMedicationHold, listScheduledHolds, scheduleHold,
  updateScheduledHold, cancelScheduledHold,
  discontinueMedication,
};
